
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EmpresaLayout, LayoutFormData } from '@/types/layout';
import { useToast } from '@/hooks/use-toast';

export const useEmpresaLayout = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: layoutConfig, isLoading, error } = useQuery({
    queryKey: ['empresa-layout'],
    queryFn: async () => {
      console.log('🔍 Buscando configurações de layout...');
      const { data, error } = await supabase
        .from('empresa_layout')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('❌ Erro ao buscar layout:', error);
        throw error;
      }

      console.log('✅ Layout encontrado:', data);
      return data as EmpresaLayout | null;
    },
    retry: false
  });

  const updateLayoutMutation = useMutation({
    mutationFn: async (data: LayoutFormData) => {
      console.log('📝 Iniciando atualização do layout:', data);
      
      if (!layoutConfig?.id) {
        console.error('❌ ID do layout não encontrado');
        throw new Error('Layout config not found');
      }

      const { error } = await supabase
        .from('empresa_layout')
        .update({
          nome_empresa: data.nome_empresa,
          logo_url: data.logo_url || null,
          cor_primaria: data.cor_primaria,
          cor_secundaria: data.cor_secundaria,
          cor_accent: data.cor_accent
        })
        .eq('id', layoutConfig.id);

      if (error) {
        console.error('❌ Erro ao atualizar layout:', error);
        throw error;
      }
      
      console.log('✅ Layout atualizado com sucesso');
    },
    onSuccess: () => {
      console.log('🎉 Sucesso na mutação');
      toast({
        title: 'Configurações salvas',
        description: 'As configurações de layout foram atualizadas com sucesso.',
      });
      queryClient.invalidateQueries({ queryKey: ['empresa-layout'] });
    },
    onError: (error) => {
      console.error('💥 Erro na mutação:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao salvar as configurações de layout.',
        variant: 'destructive',
      });
    }
  });

  return {
    layoutConfig,
    isLoading,
    error,
    updateLayout: updateLayoutMutation.mutate,
    isUpdating: updateLayoutMutation.isPending
  };
};
